// 261010 Epic 2 / P2a — tóm tắt dữ liệu CHỈ ĐỌC cho trang chi tiết video của khung mới. THUẦN (không import antd/router) để jest chạy được.
import type { ApprovalState, FilmDialogueLine, FilmEvent, FilmShot, FilmVideoClip, PositionMap, PositionMapsResponse, FilmEventBeatMeta } from '../../types/film';
import type { WorkflowStepKey, WorkflowStepState } from '../../types/filmWorkflow';

const FACING: Record<string, string> = {
  toward_camera: 'hướng về máy', away_from_camera: 'quay lưng với máy', side: 'nghiêng', none: 'không có hướng rõ',
};
/** Hướng mặt chủ thể của shot (`screenDirection.subjectFacing`, object hoặc chuỗi JSON). Không đọc được ⇒ "chưa có" — không đoán. */
export function subjectFacingLabel(screenDirection: unknown): string {
  let v: unknown = screenDirection;
  if (typeof v === 'string') { try { v = JSON.parse(v); } catch { return 'chưa có'; } }
  if (!v || typeof v !== 'object' || Array.isArray(v)) return 'chưa có';
  const f = (v as Record<string, unknown>).subjectFacing;
  return typeof f === 'string' && FACING[f] ? FACING[f] : 'chưa có';
}

export const shotLine = (s: Pick<FilmShot, 'shotSize' | 'angle' | 'cameraMove'>): string => {
  const parts = [s.shotSize, s.angle, s.cameraMove].map((x) => String(x ?? '').trim()).filter(Boolean);
  return parts.length ? parts.join(' · ') : 'chưa có thông số máy';
};

export function beatLines(beatMeta: FilmEventBeatMeta | null | undefined): string[] {
  const raw = (beatMeta as any)?.actionBeats;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((b: any) => b && typeof b.order === 'number' && typeof b.text === 'string')
    .sort((a: any, b: any) => a.order - b.order)
    .map((b: any) => `${b.order}. ${b.text}`);
}

/** 261010 P8c — nhãn nhịp truyện của shot (`beatOrders` + chữ nhịp). Vắng mảng ⇒ rỗng (không bịa). */
export function beatLinkText(beatOrders: number[] | null | undefined, beatMeta: FilmEventBeatMeta | null | undefined): string {
  if (!Array.isArray(beatOrders)) return '';
  if (beatOrders.length === 0) return 'không thuộc nhịp nào (cảnh mở / cận đồ vật)';
  const byN = new Map<number, string>();
  for (const line of beatLines(beatMeta)) {
    const m = /^(\d+)\.\s*(.*)$/.exec(line);
    if (m) byN.set(Number(m[1]), m[2]);
  }
  return beatOrders.map((n) => {
    const t = byN.get(n);
    return t ? `${n} — «${t}»` : `(nhịp ${n})`;
  }).join(', ');
}

export function dialogueLine(l: Pick<FilmDialogueLine, 'speakerName' | 'textEn'>): string {
  const text = String(l.textEn ?? '').trim();
  if (!text) return '';
  const who = String(l.speakerName ?? '').trim();
  return who ? `${who}: ${text}` : text;
}

/** Clip được chọn của event (chỉ khi có trong `clips`). */
export function selectedClipOf(ev: Pick<FilmEvent, 'selectedClipId' | 'clips'>): FilmVideoClip | null {
  if (!ev?.selectedClipId) return null;
  return (ev.clips || []).find((c) => c.id === ev.selectedClipId) ?? null;
}
export const clipStatusLabel = (s: string | null | undefined): string => {
  if (!s) return 'không rõ';
  return ({ PENDING: 'đang tạo', DONE: 'xong', FAILED: 'lỗi' } as Record<string, string>)[s] ?? s;
};

/** "Có kết quả" ≠ "người đã duyệt": nhãn duyệt chỉ nói về người bấm duyệt. */
export const approvalLabel = (s: ApprovalState | null | undefined): string =>
  s === 'APPROVED' ? 'người đã duyệt' : s === 'OUTDATED' ? 'đã duyệt nhưng đã đổi — duyệt mất hiệu lực' : 'chưa duyệt';

export const stalenessLabel = (s: string | null | undefined): string => ({
  FRESH: 'còn mới', INPUT_CHANGED: 'đầu vào đã đổi', ANCHOR_REGENERATED: 'sơ đồ gốc đã gen lại', NO_SHEET: 'chưa có ảnh', NO_IMAGE: 'chưa có ảnh',
} as Record<string, string>)[String(s)] ?? String(s ?? '');

export const driftLabel = (r: string): string => ({
  SHOT_GONE: 'khung trỏ tới shot không còn', PHASE_GONE: 'khung trỏ tới giai đoạn không còn', MAP_GONE: 'khung trỏ tới sơ đồ không còn',
  DURATION_CHANGED: 'thời lượng video đã đổi', SUBJECT_FACING_CHANGED: 'hướng mặt chủ thể của shot đã đổi',
} as Record<string, string>)[r] ?? r;

/** Các giai đoạn của một event kèm sơ đồ đã liên kết. Giai đoạn chưa liên kết ⇒ `map: null`. Không có giai đoạn ⇒ một dòng "cả video" (`phaseOrder: null`). */
export function mapsOfEvent(resp: PositionMapsResponse | null | undefined, eventId: string, phaseOrders: number[]): Array<{ phaseOrder: number | null; map: PositionMap | null }> {
  const stages: Array<number | null> = phaseOrders.length ? phaseOrders : [null];
  return stages.map((phaseOrder) => {
    const link = resp?.links?.valid?.find((l) => l.eventId === eventId && (l.phaseOrder ?? null) === phaseOrder);
    const map = link ? resp?.maps?.find((m) => m.id === link.positionMapId) ?? null : null;
    return { phaseOrder, map };
  });
}

export const stepStateNote = (s: WorkflowStepState): string => ({
  NOT_READY: 'Bước này chưa có kết quả.',
  RUNNING: 'Bước này đang chạy ở nền.',
  ERROR: 'Lần chạy gần nhất của bước này bị lỗi — xem Lịch sử để biết lý do.',
  STALE: 'Kết quả của bước này đã cũ so với đầu vào hiện tại.',
  READY: 'Đã có kết quả — chưa có người duyệt (hoặc bước này không có bước duyệt).',
  APPROVED: 'Có kết quả và có người đã duyệt, còn khớp đầu vào hiện tại.',
} as Record<WorkflowStepState, string>)[s];

/** Chỉ Sơ đồ và Storyboard có cổng duyệt thật (API + kiểm hash). Bước khác nói thẳng "chưa có" — không giả nút duyệt. */
export const APPROVABLE_STEPS: WorkflowStepKey[] = ['MAP', 'STORYBOARD'];
export const approvalNoteOf = (step: WorkflowStepKey): string | null => (APPROVABLE_STEPS.includes(step) ? null : 'Duyệt: chưa có ở bản này.');
